// src/app/shared/services/logger.service.ts
import { Injectable } from '@angular/core';
import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface LogDB extends DBSchema {
  log: { key: number; value: { ts: string; msg: string } };
}

@Injectable({ providedIn: 'root' })
export class LoggerService {
  /* ======= 原本 localStorage 欄位 ======= */
  private lsKey = 'appLogs';
  private max = 5000;

  /* ======= 新增 IndexedDB 欄位 ======= */
  private dbPromise: Promise<IDBPDatabase<LogDB>> | null = null;

  constructor() {
    /* 延遲初始化：若瀏覽器不支援 idb，catch 之後照常使用 localStorage */
    this.dbPromise = openDB<LogDB>('logs', 1, {
      upgrade(db) {
        db.createObjectStore('log', { keyPath: 'id', autoIncrement: true });
      },
    }).catch(err => {
      console.warn('IndexedDB unavailable → fallback to localStorage', err);
      return null as any; // 之後呼叫時直接 skip
    });
  }

  /* ---------- 寫入 ---------- */
  async push(msg: string): Promise<void> {
    const line = `${new Date().toISOString()}  ${msg}`;

    /* 1) localStorage */
    const ls = JSON.parse(localStorage.getItem(this.lsKey) || '[]');
    ls.push(line);
    if (ls.length > this.max) ls.shift();
    localStorage.setItem(this.lsKey, JSON.stringify(ls));

    /* 2) IndexedDB（若可用） */
    if (this.dbPromise) {
      try {
        const db = await this.dbPromise;
        if (db) await db.add('log', { ts: new Date().toISOString(), msg });
      } catch (e) {
        console.warn('IDB write failed, still OK:', e);
      }
    }
  }

  /* ---------- 讀出全部 ---------- */
  async getAll(): Promise<string> {
    /* 先嘗試 IndexedDB，成功就用它；否則回到 localStorage */
    if (this.dbPromise) {
      try {
        const db = await this.dbPromise;
        if (db) {
          const all = await db.getAll('log');
          return all.map(r => `${r.ts}  ${r.msg}`).join('\n');
        }
      } catch {
        /* fallthrough */
      }
    }
    return (
      JSON.parse(localStorage.getItem(this.lsKey) || '[]') as string[]
    ).join('\n');
  }

  /* ---------- 清空 ---------- */
  async clear(): Promise<void> {
    localStorage.removeItem(this.lsKey);
    if (this.dbPromise) {
      try {
        const db = await this.dbPromise;
        if (db) await db.clear('log');
      } catch {
        /* ignore */
      }
    }
  }
}
